/**
 * EstimateFieldset - Three-input form fieldset for capturing the
 * estimate on a Work Order or Inspection.
 *
 * Field semantics:
 *   - estimatedCost : single-point estimate. The "normal" case.
 *   - estimateMin   : optional lower bound used only when scope is
 *                     uncertain. Typically left blank.
 *   - estimateMax   : optional upper bound that must be ≥ min when
 *                     both are provided (server enforces this with
 *                     a `gte:estimate_min` validation rule).
 *
 * Rendered as a controlled form section that the parent (JobForm)
 * composes alongside its existing fieldsets (CompanyLocationFieldset,
 * AssigneeFieldset, ContactFieldset). Inputs are native
 * <input type="number"> so mobile keyboards open the numeric pad.
 *
 * The component is purely about input — it has no opinion about
 * approval state. Approval is captured separately on the detail page
 * via EstimateApprovalControl.
 *
 * @example
 * <EstimateFieldset
 *     value={{ estimatedCost: '1250', estimateMin: '', estimateMax: '' }}
 *     onChange={setEstimate}
 * />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Controlled value for the fieldset. Strings are used directly so
 * empty inputs round-trip cleanly (a numeric type would coerce
 * empties to NaN/0). The parent submits these as numeric strings to
 * the backend, which casts them to decimal(10,2).
 */
export interface EstimateFieldsetValue {
    estimatedCost: string;
    estimateMin: string;
    estimateMax: string;
}

/**
 * Props for EstimateFieldset.
 *
 * @property value      Current fieldset value (controlled)
 * @property onChange   Called with the next value on any field change
 * @property disabled   When true, all inputs are read-only + dimmed
 * @property className  Optional extra classes for the outer wrapper
 */
export interface EstimateFieldsetProps {
    value: EstimateFieldsetValue;
    onChange: (next: EstimateFieldsetValue) => void;
    disabled?: boolean;
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const inputBase =
    'block w-full rounded-md border px-12px py-10px text-14px font-sans focus:outline-none focus:ring-2 focus:ring-brand-primary border-brand-border focus:border-brand-primary disabled:bg-brand-surface disabled:opacity-60';
const labelBase = 'block text-14px font-sans font-medium text-gray-700 mb-4px';

export const EstimateFieldset: React.FC<EstimateFieldsetProps> = ({
    value,
    onChange,
    disabled = false,
    className,
}) => {
    /** Lift a partial change onto the full controlled value. */
    const update = (patch: Partial<EstimateFieldsetValue>) => {
        onChange({ ...value, ...patch });
    };

    return (
        <div className={['estimate-fieldset space-y-12px', className ?? ''].join(' ')} data-ref="molecule-estimate-fieldset">
            {/* Single-point estimate — the primary field */}
            <div className="estimate-fieldset__field">
                <label htmlFor="estimated-cost" className={labelBase}>
                    Estimated Cost (optional)
                </label>
                <div className="relative">
                    <span className="absolute left-12px top-1/2 -translate-y-1/2 text-14px text-brand-text-muted pointer-events-none">$</span>
                    <input
                        id="estimated-cost"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={value.estimatedCost}
                        onChange={(e) => update({ estimatedCost: e.target.value })}
                        disabled={disabled}
                        placeholder="0.00"
                        className={`${inputBase} pl-24px`}
                    />
                </div>
                <p className="mt-4px text-12px font-sans text-brand-text-muted">
                    Enter a single number for typical jobs.
                </p>
            </div>

            {/* Optional min/max range — for the rare case where scope
                is uncertain. Rendered as two side-by-side fields with
                shared help text above. */}
            <div className="estimate-fieldset__field estimate-fieldset__field--range">
                <label className={labelBase}>
                    Estimate Range <span className="text-12px text-brand-text-muted font-normal">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-12px">
                    <div className="relative">
                        <span className="absolute left-12px top-1/2 -translate-y-1/2 text-14px text-brand-text-muted pointer-events-none">$</span>
                        <input
                            id="estimate-min"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={value.estimateMin}
                            onChange={(e) => update({ estimateMin: e.target.value })}
                            disabled={disabled}
                            placeholder="Min"
                            aria-label="Estimate minimum"
                            className={`${inputBase} pl-24px`}
                        />
                    </div>
                    <div className="relative">
                        <span className="absolute left-12px top-1/2 -translate-y-1/2 text-14px text-brand-text-muted pointer-events-none">$</span>
                        <input
                            id="estimate-max"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={value.estimateMax}
                            onChange={(e) => update({ estimateMax: e.target.value })}
                            disabled={disabled}
                            placeholder="Max"
                            aria-label="Estimate maximum"
                            className={`${inputBase} pl-24px`}
                        />
                    </div>
                </div>
                <p className="mt-4px text-12px font-sans text-brand-text-muted">
                    Use only when the scope is uncertain (e.g. unknown
                    repair extent until on-site).
                </p>
            </div>
        </div>
    );
};

export default EstimateFieldset;
